// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectLoading } from 'redux/slices/loadingSlice';
import {
  getApiPath,
  getBrightnessFromColor,
  mergeClasses,
  normalizeNumber,
} from 'util/commons';
import { handleError } from 'util/errorHandler';
import { EmissionQRScannedResponse } from 'util/Types';
import styles from './EmissionQRScanning.module.scss';
import { toast } from 'react-toastify';
import { getFormatDateWithCurrentTimezone } from 'ts/formatDate';
import {
  DATE_FORMAT,
  TEMPORARY_STORAGE_COLORS_PALETTE,
} from 'util/ConstantValues';
import BookingScannedTable from 'components/molecules/BookingScannedTable';
import { SelectField, SelectOption } from 'components/molecules/SelectField';
import { Form } from 'react-final-form';
import { createAxios } from 'ts/createAxios';
import { API_ENDPOINTS } from 'util/endPoints';
import { getTemporaryStorages } from 'apis/temporary_storage';
import QrBarcodeScanner from 'components/atoms/QrBarcodeScanner';
import { IconArrowLeft } from 'components/atoms/icons/IconArrowLeft';
import BigNumber from 'bignumber.js';

type EmissionQRScannedData = Omit<
  EmissionQRScannedResponse,
  'wasteUnit' | 'tenant' | 'site' | 'category'
> & {
  qrCode: string;
  emissionId: string;
  wasteUnitName?: string;
  disabled?: boolean;
};

const EmissionQRScanning = () => {
  const { t } = useTranslation();
  const [scannedData, setScannedData] = useState<
    Record<string, EmissionQRScannedData>
  >({});
  const [scannedCode, setScannedCode] = useState<string>();
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [selectedTemporaryStorage, setSelectedTemporaryStorage] = useState<
    number | null
  >(null);
  const [temporaryStorageOptions, setTemporaryStorageOptions] = useState<
    SelectOption[]
  >([]);
  const [scannerShown, setScannerShown] = useState(true);

  const { isLoading } = useSelector(selectLoading);
  const resources = useMemo(() => Object.values(scannedData), [scannedData]);
  const api = createAxios();

  const handleScanSuccess = useCallback((decodedText: string) => {
    if (decodedText) {
      setScannedCode(decodedText);
    } else {
      setScannedCode(undefined);
    }
  }, []);

  const getScannedData = async () => {
    if (!scannedCode || isLoading || scannedData[scannedCode]) {
      return;
    }

    try {
      const { data } = await api.get<EmissionQRScannedResponse>(
        getApiPath(API_ENDPOINTS.EMISSIONS_QR_SCANNING, { qrCode: scannedCode })
      );

      if (data.emissionStatus === 'Received') {
        toast.error(t('messages.M_resource_not_found'));

        return;
      }

      setScannedData((prev) => ({
        ...prev,
        [scannedCode]: {
          ...data,
          qrCode: scannedCode,
          wasteUnitName: data.wasteUnit?.name,
          emissionId: data.id,
        },
      }));

      setCheckedIds((prev) => [...prev, scannedCode]);
    } catch (error) {
      handleError(error);
    } finally {
      setScannedCode(undefined);
    }
  };

  const handleSubmit = async () => {
    if (!checkedIds.length) return;

    const selectedResources = resources.filter((item) =>
      checkedIds.includes(item.qrCode)
    );

    try {
      const params = selectedResources.map(({ id }) => ({
        emissionId: id,
        temporaryStorageId: selectedTemporaryStorage,
      }));

      await api.post(API_ENDPOINTS.EMISSION_TEMPORARY_STORAGES, params);

      toast.success(t('messages.M_010'));
      setScannedData((prev) => {
        const newData = { ...prev };
        selectedResources.forEach(({ qrCode }) => {
          delete newData[qrCode];
        });

        return newData;
      });
      setCheckedIds([]);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message
          ? e.response.data.message
          : t('receives.signup_messages.failed')
      );
    }
  };

  useEffect(() => {
    getScannedData();
  }, [scannedCode]);

  useEffect(() => {
    getTemporaryStorages({
      successCallback: (data) => {
        setTemporaryStorageOptions(
          data.map(({ id, name }) => ({
            label: name,
            value: Number(id),
          }))
        );
      },
    });
  }, []);

  const renderScannerControl = () => {
    const btnText = scannerShown
      ? t('emission_qr_scanning.hide_scanner')
      : t('emission_qr_scanning.show_scanner');

    return (
      <div
        className={styles.scannerToggle}
        onClick={() => setScannerShown((prev) => !prev)}
      >
        <IconArrowLeft
          className={mergeClasses(styles.arrowIcon, {
            [styles.rotated]: !scannerShown,
          })}
          color="#86ea5c"
        />
        <p>{btnText}</p>
      </div>
    );
  };

  return (
    <div className={styles.scanWrapper}>
      <div className={styles.content}>
        {scannerShown && (
          <div className={styles.qrCodeRegion}>
            <QrBarcodeScanner
              onScanSuccess={handleScanSuccess}
              classNames={{
                container: styles.scanner,
              }}
            />
          </div>
        )}

        <div className={styles.scannedContent}>
          {renderScannerControl()}

          <div className={styles.scannedContentHeader}>
            <p className={styles.scannedTitle}>
              {t('emission_qr_scanning.scanned_result')}
            </p>
            <p className={styles.scannedInfo}>
              {t('emission_qr_scanning.scanned_total_items', {
                total: normalizeNumber({
                  value: resources.length.toString(),
                }),
                weight: normalizeNumber({
                  value: resources.reduce((total, item) => {
                    return total.plus(new BigNumber(item.weight || 0));
                  }, new BigNumber(0)),
                }),
              })}
            </p>
          </div>

          <div className={styles.scannedTableWrapper}>
            <BookingScannedTable
              data={scannedData}
              checkedIds={checkedIds}
              setCheckedIds={setCheckedIds}
              columns={[
                {
                  title: t('emission_qr_scanning.item'),
                  dataIndex: 'wasteUnitName',
                  width: '20%',
                },
                {
                  title: t('emission_qr_scanning.weight'),
                  dataIndex: 'weight',
                  width: '13%',
                  render: ({ weight }) =>
                    `${normalizeNumber({ value: weight })}kg`,
                },
                {
                  title: t('emission_qr_scanning.temporary_storage'),
                  dataIndex: 'temporaryStorage',
                  width: '20%',
                  render: ({ temporaryStorage }) => {
                    if (!temporaryStorage) {
                      return null;
                    }

                    const color =
                      TEMPORARY_STORAGE_COLORS_PALETTE[
                        Number(temporaryStorage.color) %
                          TEMPORARY_STORAGE_COLORS_PALETTE.length
                      ];

                    return (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <span
                          className={styles.temporaryStorageTag}
                          style={{
                            color:
                              getBrightnessFromColor(color) <= 100
                                ? '#ffffff'
                                : '#1b1b1b',
                            backgroundColor: color,
                          }}
                        >
                          {temporaryStorage?.name}
                        </span>
                      </div>
                    );
                  },
                },
                {
                  title: t('emission_qr_scanning.storage_updated_at'),
                  dataIndex: 'storageUpdatedAt',
                  width: '14%',
                  render: ({ storageUpdatedAt }) =>
                    storageUpdatedAt
                      ? getFormatDateWithCurrentTimezone(
                          storageUpdatedAt,
                          DATE_FORMAT.slaYMDHm
                        )
                      : '',
                },
                {
                  title: t('emission_qr_scanning.emission_id'),
                  dataIndex: 'emissionId',
                  width: '20%',
                },
                {
                  title: t('emission_qr_scanning.emission_date_time'),
                  dataIndex: 'emissionDatetime',
                  width: '13%',
                  render: ({ emissionDatetime }) =>
                    getFormatDateWithCurrentTimezone(
                      emissionDatetime,
                      DATE_FORMAT.slaYMDHm
                    ),
                },
              ]}
            />
          </div>
        </div>
      </div>

      <div className={styles.scannedFooter}>
        <div className={styles.scannedFooterBottom}>
          <Form onSubmit={() => {}}>
            {() => (
              <SelectField
                name="temporaryStorageId"
                placeholder={t('emission_qr_scanning.temporary_storage')}
                options={temporaryStorageOptions}
                disabled={!temporaryStorageOptions.length}
                onChange={(value) =>
                  setSelectedTemporaryStorage(value ? Number(value) : null)
                }
                isDarkmode
                menuPlacement="top"
              />
            )}
          </Form>
          <button
            className={styles.btnRequest}
            type="button"
            disabled={!checkedIds.length}
            onClick={handleSubmit}
          >
            {t('common.button.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmissionQRScanning;
